import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Base win fixture with gamble offered (5×4 tumbling reels — the template's 6×5 layout never played
 * on this grid). possibleClientActions includes "gamble" so the post-win gamble flow is offered;
 * gambleOffer declares what the server offers for this round: the win to stake (the round's 200, the
 * only stake the backend accepts) and the limits. An H2 cluster wins 200 (100 × 2 from the cannon),
 * tumbles away, the collapse settles with no further win.
 */
export function baseFixtureGamble(): SpinResponse {
  const win = mask([10, 11, 15, 16, 17]); // H2 ×5 (bottom-left block)
  const amountMinor = "200";

  return spinFixture("base-gamble-001")
    .balance("EUR", "100000")
    .clientActions(["gamble"])
    .gambleOffer({
      gambleType: "post_win",
      currentWinAmountMinor: amountMinor,
      options: [
        { optionId: "color_red", label: "Red", multiplier: "2", probability: "0.50", group: "color" },
        { optionId: "color_black", label: "Black", multiplier: "2", probability: "0.50", group: "color" },
      ],
      maxAttemptsRemaining: 3,
      gambleCeilingMinor: "500000",
    })
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L4", "L5", "L2", "H1", "L3"],
        ["L3", "L4", "L5", "L2", "L5"],
        ["H2", "H2", "L4", "L5", "L2"],
        ["H2", "H2", "H2", "L3", "L4"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win, winAmount: amountMinor, symbolIdentifier: "H2", symbolCount: "5", // 100 × 2 = 200
        lines: [{ positionMask: win, winAmount: amountMinor, baseWinAmount: "100", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win" });

      const fire = cannonFire(0);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire" });

      const r = fx.removeSymbolsByMask(win);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove" });

      const t = fx.updateTotalWinMeter(amountMinor);
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total" });
    })
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L2", "L3", "L4", "H1", "L3"],
        ["L4", "L5", "L2", "L2", "L5"],
        ["L4", "L5", "L5", "L5", "L2"],
        ["L3", "L4", "L4", "L3", "L4"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", amountMinor);
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
